import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Routes } from './routes';
import NoModule from '@/components/NoModule';
import * as Core from '@/components/core';
import * as Helpdesk from '@/components/helpdesk';
import { CulturalCapabilities, RelativesInfoPage, SurveyParticipatePage, SurveysPage } from '@/components/survey';
import {
  SelfPage,
  DailySalePage,
  ReserveProgram,
  NurtureTeammate,
  ReserveReportPage
} from '@/components/reserve';
import {
  DormitoryAttendanceReport,
  DormitoryCardexes,
  DormitoryLeave,
  DormitoryLeaveReport,
  DormitoryRegister,
  DormitoryRegisterGroupSelectRoom,
  DormitoryRegisterSolitarySelectRoom
} from '@/components/dormitory_legacy';
import { DormitoryPreRegisterGate } from '@/components/dormitory';
import { DormitoryPreRegisterPaymentCallback } from '@/components/dormitory/DormitoryPreRegisterPaymentCallback';
import { ElectionCandidacy } from '@/components/survey/ElectionCandidacy';
import { Elections } from '@/components/survey/Elections';
import { Competition } from '@/components/survey/Competition';
import { Camp } from '@/components/survey/Camp';
import { Workshops } from '@/components/survey/Workshops/Workshops';
import { GraduationCelebrations } from '@/components/survey/GraduationCelebrations/GraduationCelebrations';
import { SportCapabilities } from '@/components/sport/SportCapabilities/SportCapabilities';
import { ProgramContextProvider } from '@/context/ProgramContext/ProgramContext';

interface Route {
  path: any;
  breadcrumb: string | any[];
  element: JSX.Element | null;
}

export default function useRoutes() {
  const navigate = useNavigate();

  const [t] = useTranslation(['common']);

  const breadNurture = useMemo(
    () => <span onClick={() => navigate(Routes.Food)}>{t('common:labels.nurture')}</span>,
    [navigate, t]
  );

  const breadSport = useMemo(
    () => <span onClick={() => navigate(Routes.Sport)}>{t('common:labels.sport')}</span>,
    [navigate, t]
  );

  const breadDormitory = useMemo(
    () => <span onClick={() => navigate(Routes.Dormitory)}>{t('common:labels.dormitory')}</span>,
    [navigate, t]
  );

  const breadHelpDesk = useMemo(
    () => <span onClick={() => navigate(Routes.Helpdesk)}>{t('common:labels.helpdesk')}</span>,
    [navigate, t]
  );

  const breadHealth = useMemo(
    () => <span onClick={() => navigate(Routes.Health)}>{t('common:labels.health')}</span>,
    [navigate, t]
  );

  const breadTransport = useMemo(
    () => <span onClick={() => navigate(Routes.Transport)}>{t('common:labels.transport')}</span>,
    [navigate, t]
  );

  const breadCultural = useMemo(
    () => <span onClick={() => navigate(Routes.Surveys)}>{t('common:labels.cultural')}</span>,
    [navigate, t]
  );

  const routes: Route[] = useMemo(() => {
    return [
      { path: Routes.Home, element: <Core.HomePage />, breadcrumb: null },
      { path: Routes.Food, element: <Core.HomePage />, breadcrumb: null },
      { path: Routes.Helpdesk, element: <Core.HomePage />, breadcrumb: null },
      { path: Routes.Transport, element: <Core.HomePage />, breadcrumb: null },
      { path: Routes.Sport, element: <Core.HomePage />, breadcrumb: null },
      { path: Routes.Health, element: <Core.HomePage />, breadcrumb: null },
      { path: Routes.Dormitory, element: <Core.HomePage />, breadcrumb: null },
      { path: Routes.Face, element: <Core.HomePage />, breadcrumb: null },
      { path: Routes.Surveys, element: <Core.HomePage />, breadcrumb: null },
      {
        path: Routes.Announcements,
        element: <Core.AnnouncementsPage />,
        breadcrumb: [t('common:labels.announcements')],
      },
      {
        path: Routes.FoodSelfs,
        element: <SelfPage />,
        breadcrumb: [breadNurture, t('common:labels.self')],
      },
      {
        path: Routes.UserWallet,
        element: <Core.WalletPage />,
        breadcrumb: [t('common:labels.wallet')],
      },
      {
        path: Routes.UserCart,
        element: <Core.CartPage />,
        breadcrumb: [t('common:labels.cart')],
      },
      {
        path: Routes.SportGyms,
        element: <SelfPage />,
        breadcrumb: [breadSport, t('common:labels.gym')],
      },
      {
        path: Routes.HealthClinics,
        element: <SelfPage />,
        breadcrumb: [breadSport, t('common:labels.specialist')],
      },
      {
        path: Routes.TransportTerminals,
        element: <SelfPage />,
        breadcrumb: [breadTransport, t('common:labels.select-origin')],
      },
      {
        path: Routes.FoodReserve,
        element: <ProgramContextProvider><ReserveProgram variant="nurture" /></ProgramContextProvider>,
        breadcrumb: [breadNurture, t('common:labels.food-reservation')],
      },
      {
        path: Routes.SportReserve,
        element: <ProgramContextProvider><ReserveProgram variant="sport" /></ProgramContextProvider>,
        breadcrumb: [breadSport, t('common:labels.ticket')],
      },
      {
        path: Routes.HealthReserve,
        element: <ProgramContextProvider><ReserveProgram variant="health" /></ProgramContextProvider>,
        breadcrumb: [breadHealth, t('common:labels.clinic-reservation')],
      },
      {
        path: Routes.TransportReserve,
        element: <ProgramContextProvider><ReserveProgram variant="transport" /></ProgramContextProvider>,
        breadcrumb: [breadTransport, t('common:labels.buy-ticket')],
      },
      {
        path: Routes.FoodReservesReport,
        element: <ReserveReportPage variant="nurture" />,
        breadcrumb: [breadNurture, t('common:features.reserve-status')],
      },
      {
        path: Routes.SportReservesReport,
        element: <ReserveReportPage variant="sport" />,
        breadcrumb: [breadNurture, t('common:features.reserve-status')],
      },
      {
        path: Routes.SportCapabilities,
        element: <SportCapabilities />,
        breadcrumb: [breadSport, t('common:features.sport-capabilities')],
      },
      {
        path: Routes.HealthReservesReport,
        element: <ReserveReportPage variant="health" />,
        breadcrumb: [breadHealth, t('common:features.reserve-status')],
      },
      {
        path: Routes.TransportReservesReport,
        element: <ReserveReportPage variant="transport" />,
        breadcrumb: [breadTransport, t('common:features.reserve-status')],
      },
      {
        path: Routes.UserProfile,
        element: <Core.ProfilePage />,
        breadcrumb: [t('common:labels.profile')],
      },
      {
        path: Routes.FoodDailySale,
        element: <DailySalePage variant="nurture" />,
        breadcrumb: [breadNurture, t('common:labels.daily-sale')],
      },
      {
        path: Routes.SportDailySale,
        element: <DailySalePage variant="sport" />,
        breadcrumb: [breadSport, t('common:features.last-minute')],
      },
      {
        path: Routes.TransportDailySale,
        element: <DailySalePage variant="transport" />,
        breadcrumb: [breadTransport, t('common:features.last-minute')],
      },
      {
        path: Routes.HealthDailySale,
        element: <DailySalePage variant="health" />,
        breadcrumb: [breadHealth, t('common:features.last-minute')],
      },
      {
        path: Routes.UserActivityReport,
        element: <Core.ActivityReportPage />,
        breadcrumb: [breadNurture, t('common:labels.activity-report')],
      },
      {
        path: Routes.FoodTeammate,
        element: <NurtureTeammate />,
        breadcrumb: [breadNurture, t('common:features.teammate')],
      },
      {
        path: Routes.SurveyCulturalCapabilities,
        element: <CulturalCapabilities />,
        breadcrumb: [breadCultural, t('common:features.survey-CULTURAL_CAPABILITIES')],
      },
      {
        path: Routes.SurveyWorkshops,
        element: <Workshops />,
        breadcrumb: [breadCultural, t('common:features.survey-WORKSHOPS')],
      },
      {
        path: Routes.SurveyGraduationCelebrations,
        element: <GraduationCelebrations />,
        breadcrumb: [breadCultural, t('common:features.survey-GRADUATION_CELEBRATIONS')],
      },
      {
        path: Routes.SurveyElectionCandidacy,
        element: <ElectionCandidacy />,
        breadcrumb: [breadCultural, t('common:features.survey-ELECTION_CANDIDACY')],
      },
      {
        path: Routes.SurveyElections,
        element: <Elections />,
        breadcrumb: [breadCultural, t('common:features.survey-ELECTION')],
      },
      {
        path: Routes.SurveyCompetition,
        element: <Competition />,
        breadcrumb: [breadCultural, t('common:features.survey-EXAM')],
      },
      {
        path: Routes.SurveyCamp,
        element: <Camp />,
        breadcrumb: [breadCultural, t('common:features.survey-CAMPS')],
      },
      {
        path: Routes.SurveyParticipate,
        element: <SurveyParticipatePage />,
        breadcrumb: [breadCultural, t('common:labels.event')],
      },
      {
        path: Routes.SurveySurvey,
        element: <SurveysPage />,
        breadcrumb: [breadCultural, t('common:features.survey-SURVEY')],
      },

      {
        path: Routes.SurveyReletivesInfo,
        element: <RelativesInfoPage />,
        breadcrumb: [breadCultural, t('common:labels.relatives-info')],
      },
      {
        path: Routes.UserMessages,
        element: <Core.MessagesListPage />,
        breadcrumb: [t('common:labels.message')],
      },
      {
        path: Routes.UserMessageView,
        element: <Core.MessageDetailPage />,
        breadcrumb: [t('common:labels.message-details')],
      },
      /*
      {
        path: Routes.UserFeedbacks,
        element: <Core.FeedbacksPage />,
        breadcrumb: [t('common:labels.feedback')],
      },
      {
        path: Routes.UserFeedback,
        element: <Core.FeedbackDetailPage />,
        breadcrumb: [t('common:labels.feedback-details')],
      },
      */
      {
        path: Routes.DormitoryAttendanceReport,
        element: <DormitoryAttendanceReport />,
        breadcrumb: [t('common:features.attendance-report')],
      },
      {
        path: Routes.DormitoryCardexes,
        element: <DormitoryCardexes />,
        breadcrumb: [t('common:features.cardex-accommodation')],
      },
      {
        path: Routes.FAQ,
        element: <Core.FAQPage />,
        breadcrumb: [t('common:labels.common-questions')],
      },
      {
        path: Routes.DormitoryPreRegister,
        element: <DormitoryPreRegisterGate />,
        breadcrumb: [breadDormitory, t('common:features.dormitory-pre-register')],
      },
      {
        path: Routes.DormitoryPreRegisterPaymentCallback,
        element: <DormitoryPreRegisterPaymentCallback />,
        breadcrumb: [breadDormitory],
      },
      {
        path: Routes.DormitoryRegister,
        element: <DormitoryRegister />,
        breadcrumb: [breadDormitory, t('common:features.dormitory-register')],
      },
      {
        path: Routes.DormitoryGroupRoomSelector,
        element: <DormitoryRegisterGroupSelectRoom />,
        breadcrumb: [
          breadDormitory,
          <span key="drom-signup" onClick={() => navigate(Routes.DormitoryRegister)}>
            {t('common:features.dormitory-register')}
          </span>,
          t('common:labels.choose-room'),
        ],
      },
      {
        path: Routes.DormitorySolitrayRoomSelector,
        element: <DormitoryRegisterSolitarySelectRoom />,
        breadcrumb: [
          breadDormitory,
          <span key="drom-signup" onClick={() => navigate(Routes.DormitoryRegister)}>
            {t('common:features.dormitory-register')}
          </span>,
          t('common:labels.choose-room'),
        ],
      },
      {
        path: Routes.DormitoryLeave,
        element: <DormitoryLeave />,
        breadcrumb: [breadDormitory, t('common:features.dormitory-leave-registeration')],
      },
      {
        path: Routes.DormitoryLeaveReport,
        element: <DormitoryLeaveReport />,
        breadcrumb: [breadDormitory, t('common:features.dormitory-leave-report')],
      },
      {
        path: Routes.HelpdeskIssues,
        element: <Helpdesk.RequestListPage />,
        breadcrumb: [breadHelpDesk, t('common:features.view-request')],
      },
      {
        path: Routes.HelpdeskViewIssue,
        element: <Helpdesk.IssueDetailsPage />,
        breadcrumb: [breadHelpDesk, t('common:labels.request-details')],
      },
      {
        path: Routes.HelpdeskCreateIssue,
        element: <Helpdesk.CreateIssuePage />,
        breadcrumb: [breadHelpDesk, t('common:labels.submit-request')],
      },
      {
        path: Routes.ModuleInactive,
        element: <NoModule />,
        breadcrumb: null,
      },
    ];
  }, [
    breadCultural,
    breadDormitory,
    breadHealth,
    breadNurture,
    breadSport,
    breadTransport,
    breadHelpDesk,
    navigate,
    t
  ]);

  return routes;
}
